// Note: this component is also referred to as Publications Listing in some places.

import * as R from "ramda";

import { arrayOf, bool, number, shape, string } from "prop-types";
import {
  createFacetString,
  createPageString,
  initFacetsFromQs,
  updateFacets,
  updateQueryParams,
  updateResponse,
} from "../../common/helpers.js";
import { useEffect, useRef, useState } from "react";
import cx from "classnames";

import DropdownFacets from "../DropdownFacets/DropdownFacets.jsx";
import Loader from "../Loader/Loader.jsx";
import RichText from "../RichText/RichText.jsx";
import { SearchBar } from "./SearchBar.jsx";
import { SelectedFacets } from "./SelectedFacets.jsx";
import { mockApiResponse } from "./mockData.js";
import useMatchMedia from "../../common/useMatchMedia.js";

const propTypes = {
  storybook: bool,
  index: number,
  dataModel: shape({
    url: string.isRequired,
    initialResults: number.isRequired,
    perPage: number.isRequired,
    loadMore: bool.isRequired,
    loadMoreText: string.isRequired,
    includeSearchBar: bool.isRequired,
    searchPlaceholder: string,
    showFacetsLabel: string.isRequired,
    clearAllLabel: string.isRequired,
    facetKeys: arrayOf(string),
    columnOneText: string,
    columnTwoText: string,
    excludeFromQueryString: bool,
    header: string,
    description: string,
  }).isRequired,
  dataQuery: shape({
    pageId: string.isRequired,
    listingId: string.isRequired,
    showType: bool.isRequired,
  }).isRequired,
};

const AcademicAndResearchListing = (props) => {
  const {
    storybook,
    dataModel: {
      url,
      loadMore,
      loadMoreText,
      includeSearchBar,
      searchPlaceholder,
      clearAllLabel,
      facetKeys,
      columnOneText,
      columnTwoText,
      excludeFromQueryString,
      header,
      description,
    },
    dataQuery: { pageId, listingId, showType },
  } = props;

  // Media queries
  const matchesMedium = useMatchMedia("(min-width: 746px)");
  const matchesLarge = useMatchMedia("(min-width: 1200px)");

  // States
  // Used to show and hide mobile facets full-screen takeover menu
  const [showMobileFacets, setShowMobileFacets] = useState(false);

  // Handle special case where we may have a search term coming from global search
  const initialUrl = new URL(window.location.href);
  const initialParams = new URLSearchParams(initialUrl.search);
  const initialQueryParam = initialParams.get("q");

  // Used to keep track of entered search term
  const [searchTerm, setSearchTerm] = useState(initialQueryParam ?? "");

  // Keep track of loading state and API response
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState({});

  // Used to keep track of selected/active facets
  const [activeFacets, setActiveFacets] = useState({
    ...initFacetsFromQs(facetKeys),
    page: 1,
  });

  // Refs
  const searchRef = useRef(null);
  const facetStringRef = useRef();

  // useEffect block
  useEffect(() => {
    setIsLoading(true);
    const searchString = searchTerm ? "&q=" + searchTerm : "";
    const facetString = createFacetString(activeFacets) + searchString;
    const pageString = createPageString(activeFacets);

    if (storybook === true) {
      // Pull the results from our mock API data
      const apiResponse = mockApiResponse;

      setResponse((prevState) =>
        R.mergeDeepWithKey(
          (k, l, r) => (k === "results" ? R.concat(l, r) : r),
          prevState,
          apiResponse,
        ),
      );

      facetStringRef.current = facetString;
      updateQueryParams(facetKeys, activeFacets);
      setIsLoading(false);
    } else {
      // Fetch the results from the API
      fetch(
        `${url}?listingId=${listingId}&pageId=${pageId}${facetString ? facetString : ""
        }&${searchString ? "&page=1" : pageString}`,
      )
        .then((response) => response.json())
        .then((data) =>
          setResponse((prevState) =>
            updateResponse(facetString, facetStringRef, data, prevState),
          ),
        )
        .catch((e) => console.warn(e))
        .finally(() => {
          facetStringRef.current = facetString;
          updateQueryParams(facetKeys, activeFacets, true, searchTerm);
          setIsLoading(false);
        });
    }
  }, [
    url,
    searchTerm,
    listingId,
    pageId,
    loadMore,
    activeFacets,
    facetKeys,
    storybook,
    excludeFromQueryString,
  ]);

  // Functions
  const handlePageChange = (page) => {
    setActiveFacets((prev) => R.evolve({ ["page"]: () => page }, prev));
  };

  const handleFacetClick = (facetId, valueId) =>
    setActiveFacets((prev) => {
      return updateFacets(facetId, valueId, prev);
    });

  const clearAllFacets = () => {
    setActiveFacets({ page: 1 });
  };

  // Hides load more button under table when there's no more results
  const hideLoadMore =
    Object.keys(response).length > 0 &&
    response.results.length === response.totalResults;

  // Handles when users press the enter key in the search box
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      searchRef.current.value !== searchTerm
        ? [clearAllFacets(), setSearchTerm(searchRef.current.value)]
        : null;
    }
  };

  const getDisplayYearOrStatus = (status, year) =>
    status === "Unconditionally accepted - forthcoming" ? "Forthcoming" : year;

  return (
    <section className="academic-research-listing">
      {header && (
        <div className="component__header">
          <RichText>
            <div dangerouslySetInnerHTML={{ __html: header }}></div>
          </RichText>
        </div>
      )}
      {description && (
        <div className="component__description">
          <RichText>
            <p dangerouslySetInnerHTML={{ __html: description }}></p>
          </RichText>
        </div>
      )}

      <div className="dropdown-facets">
        {matchesLarge ? (
          // Render desktop version of facet dropdown menus
          <div className="dropdown-facets__left">
            {response?.facets &&
              response?.facets.map((facet, i) => {
                // Putting each facet object in an array to easily work with
                // pre-existing functions in the Facets component
                const arr = [];
                arr.push(facet);

                return (
                  <DropdownFacets
                    key={i}
                    facets={arr}
                    onFacetClick={handleFacetClick}
                    showFacetsLabel={facet.label}
                    showDownCaret
                  />
                );
              })}
          </div>
        ) : (
          // Render mobile version of facet dropdown menu
          response?.facets && (
            <DropdownFacets
              facets={response.facets}
              facetMenuOpen={showMobileFacets}
              setFacetMenuOpen={setShowMobileFacets}
              onFacetClick={handleFacetClick}
              clearAllFacets={clearAllFacets}
              showFacetsLabel="Filters"
              clearAllLabel={clearAllLabel}
              totalResults={response?.totalResults}
              showDownCaret={false}
            />
          )
        )}

        {/* Change order of facet bubbles and search bar
            depending on monitor width */}
        {matchesLarge ? (
          <>
            <SearchBar
              includeSearchBar={includeSearchBar}
              searchPlaceholder={searchPlaceholder}
              searchRef={searchRef}
              handleKeyDown={handleKeyDown}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              searchIcon={searchIcon}
              clearAllFacets={clearAllFacets}
            />
          </>
        ) : (
          <>
            <SelectedFacets
              clearAllLabel={clearAllLabel}
              clearAllFacets={clearAllFacets}
              handleFacetClick={handleFacetClick}
              response={response}
              setShowMobileFacets={setShowMobileFacets}
              showMobileFacets={showMobileFacets}
              activeFacets={activeFacets}
            />
            <SearchBar
              includeSearchBar={includeSearchBar}
              searchPlaceholder={searchPlaceholder}
              searchRef={searchRef}
              handleKeyDown={handleKeyDown}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              searchIcon={searchIcon}
              clearAllFacets={clearAllFacets}
            />
          </>
        )}
      </div>
      {matchesLarge && (
        <SelectedFacets
          clearAllLabel={clearAllLabel}
          clearAllFacets={clearAllFacets}
          handleFacetClick={handleFacetClick}
          response={response}
          setShowMobileFacets={setShowMobileFacets}
          showMobileFacets={showMobileFacets}
          activeFacets={activeFacets}
        />
      )}

      {/* Render the table when there are results */}
      {(() => {
        if (response.results?.length > 0) {
          return (
            <table className="academic-research-listing__table">
              <thead>
                <tr>
                  {matchesMedium ? (
                    <>
                      <th>{columnOneText}</th>
                      <th>{columnTwoText}</th>
                    </>
                  ) : (
                    <th>
                      {columnOneText}/{columnTwoText}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {response.results?.map((result, i) => (
                  <tr key={i}>
                    <td className="academic-research-listing__table-type">
                      {matchesMedium ? (
                        showType ? (
                          <>
                            <div>{result.type}</div>
                            <div>{getDisplayYearOrStatus(result.status, result.year)}</div>
                          </>
                        ) : (
                          <div>{getDisplayYearOrStatus(result.status, result.year)}</div>
                        )
                      ) : showType ? (
                        <div>
                          {result.type}/{getDisplayYearOrStatus(result.status, result.year)}
                        </div>
                      ) : (
                        <div>{getDisplayYearOrStatus(result.status, result.year)}</div>
                      )}
                    </td>
                    <td className="academic-research-listing__table-publications">
                      {result.url ? (
                        <a
                          className="academic-research-listing__table-title"
                          href={result.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {result.title}
                        </a>
                      ) : (
                        <div className="academic-research-listing__table-title">
                          {result.title}
                        </div>
                      )}

                      {result.journal && (
                        <div className="academic-research-listing__table-journal">
                          {result.journal}
                        </div>
                      )}
                      <div className="academic-research-listing__table-divider"></div>

                      {result.authors && (
                        <div
                          className="academic-research-listing__table-authors"
                          dangerouslySetInnerHTML={{ __html: result.authors }}
                        ></div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        } else if (!isLoading && !response.results?.length) {
          // Render the message below when there are no results, and the page is done loading
          return (
            <p className="academic-research-listing__no-results">
              No results found.
            </p>
          );
        } else {
          return null;
        }
      })()}

      {isLoading && <Loader />}

      <div
        className={cx("academic-research-listing__cta", {
          "academic-research-listing__cta--hidden": hideLoadMore,
        })}
      >
        <button
          className="call-to-action call-to-action--outline"
          onClick={() => {
            handlePageChange(activeFacets.page + 1);
          }}
          type="button"
        >
          {loadMoreText}
        </button>
      </div>
    </section>
  );
};

// Specifies the default values for props:
AcademicAndResearchListing.defaultProps = {
  storybook: false,
};

AcademicAndResearchListing.propTypes = propTypes;
export default AcademicAndResearchListing;

const searchIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="feather feather-search"
  >
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);
